<template>
  <div class="date-picker-calendar mx-auto">
    <suspense>
      <etg-calendar
        v-model="selectedDate"
        :min-date="startDate"
        :max-date="endDate"
        :disabled-dates="calendarDisabledDates"
        :dates-append-text="calendarDatesAppendText"
        :is-required="props.isRequired"
      />
      <template #fallback>
        <etg-loading class="size-10 text-red-50 mx-auto" />
      </template>
    </suspense>
  </div>
</template>

<script setup lang="ts">
import { eachDayOfInterval, add } from 'date-fns'

import EtgLoading from '@/components/common/etg-loading.vue'

import type { BranchArrivalTimeInfo } from '@/types/api'
import type { GroupByDateSlots } from '@/types/product'

const EtgCalendar = defineAsyncComponent(() => dynamicImport(() => import('@/components/common/etg-calendar.vue'), 'etg-calendar'))

const { format } = useI18nDateFns()

const selectedDate = defineModel<Nullable<string>>({ default: null })

const props = withDefaults(
  defineProps<{
    arrivalTimes: BranchArrivalTimeInfo
    groupedByDateSlots: GroupByDateSlots
    checkOutTime?: string
    isRequired?: boolean
  }>(),
  {
    checkOutTime: '00:00:00',
    isRequired: false,
  }
)

const startDate = computed(() => getTimeZoneDate(props.arrivalTimes.start_day))
const endDate = computed(() => {
  const baseEndDate = calculateBaseEndDate(props.arrivalTimes.end_day)
  return baseEndDate
})

const datesInBetween = computed(() =>
  eachDayOfInterval({
    start: startDate.value,
    end: endDate.value,
  }).map(date => format(date, 'yyyy-MM-dd'))
)
const calendarDisabledDates = ref<string[]>([])
const updateCalendarDisabledDates = (isPadding = false) => {
  let selectableDates: string[] = []

  if (isPadding) return datesInBetween.value

  selectableDates = props.groupedByDateSlots ? Object.keys(props.groupedByDateSlots) : []
  calendarDisabledDates.value = datesInBetween.value.filter(date => !selectableDates.includes(date))
}

const calendarDatesAppendText = computed(() => {
  return Object.entries(props.groupedByDateSlots).reduce(
    (acc, [key, item]) => {
      acc[key] = `-${item.maxDiscount}%`
      return acc
    },
    {} as Record<string, string>
  )
})

const calculateBaseEndDate = (endDay: string | undefined) => {
  if (endDay) {
    return getTimeZoneDate(endDay)
  }
  const nowInTimezone = getTimeZoneDate(new Date())
  return add(nowInTimezone, { days: 30 })
}

watchEffect(() => {
  updateCalendarDisabledDates()
})
</script>
